import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Button, Input, Select } from '../../components/ui/Forms';
import { Modal } from '../../components/ui/Modal';
import SafeIcon from '../../common/SafeIcon';

export default function PaymentMethodsPanel() {
  const { state, addPaymentMethod, updatePaymentMethod, deletePaymentMethod } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const initialFormState = {
    type: 'credit_card',
    nickname: '',
    last4: '',
    expiryDate: ''
  };
  
  const [formData, setFormData] = useState(initialFormState);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      updatePaymentMethod(editingId, formData);
    } else {
      addPaymentMethod(formData);
    }
    handleClose();
  };

  const handleEdit = (pm) => {
    setFormData({ ...pm });
    setEditingId(pm.id);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  const handleDelete = (id) => {
    const isUsed = state.subscriptions.some(s => s.paymentMethodId === id);
    if (isUsed) {
      alert("This payment method is currently linked to active subscriptions. Please change them before deleting.");
      return;
    }
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      deletePaymentMethod(id);
    }
  };

  return (
    <div className="bg-white p-8 rounded-[32px] shadow-sm border border-slate-100">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-2xl">
            <SafeIcon name="CreditCard" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Payment Methods</h2>
            <p className="text-xs text-slate-500 font-medium">Manage how you pay for your subscriptions</p>
          </div>
        </div>
        <Button onClick={() => setIsModalOpen(true)} className="rounded-xl px-6">
          <SafeIcon name="Plus" /> Add Method
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {state.paymentMethods.map((pm) => (
          <div key={pm.id} className="bg-slate-50 p-5 rounded-2xl border border-slate-100 flex flex-col relative overflow-hidden group hover:bg-white hover:shadow-md hover:border-blue-200 transition-all">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2.5 bg-white rounded-xl shadow-sm group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                <SafeIcon name={pm.type === 'credit_card' ? 'CreditCard' : 'Briefcase'} className="w-5 h-5" />
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleEdit(pm)} className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                  <SafeIcon name="Edit2" className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(pm.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                  <SafeIcon name="Trash2" className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <h3 className="font-bold text-slate-800 mb-0.5">{pm.nickname}</h3>
              <p className="text-slate-500 font-mono text-xs tracking-widest">•••• {pm.last4 || '****'}</p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200/50 flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {pm.type === 'credit_card' ? 'Credit Card' : 'Bank Account'}
              </span>
              {pm.type === 'credit_card' && pm.expiryDate && (
                <span className="text-[10px] font-bold text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-100">
                  EXP: {pm.expiryDate}
                </span>
              )}
            </div>
          </div>
        ))}
        {state.paymentMethods.length === 0 && (
          <div className="col-span-full py-10 text-center border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/30">
            <p className="text-slate-400 text-sm font-medium">No payment methods saved</p>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleClose} title={editingId ? "Edit Payment Method" : "Add Payment Method"}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Select 
            label="Type" 
            options={[{ label: 'Credit Card', value: 'credit_card' }, { label: 'Bank Account', value: 'bank_account' }]} 
            value={formData.type} 
            onChange={(e) => setFormData({ ...formData, type: e.target.value })} 
            required 
          />
          <Input 
            label="Nickname (e.g. Personal Visa)" 
            value={formData.nickname} 
            onChange={(e) => setFormData({ ...formData, nickname: e.target.value })} 
            required 
          />
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Last 4 Digits" 
              maxLength={4} 
              pattern="\d{4}" 
              value={formData.last4} 
              onChange={(e) => setFormData({ ...formData, last4: e.target.value.replace(/\D/g, '') })} 
              required 
            />
            {formData.type === 'credit_card' && (
              <Input 
                label="Expiry Date (MM/YY)" 
                placeholder="MM/YY" 
                value={formData.expiryDate} 
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })} 
                required 
              />
            )}
          </div>
          <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
            <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button type="submit">{editingId ? 'Update' : 'Save'} Method</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}